import { getActiveUserProgramOptions } from "@/actions/user-program/handler";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext, useRouterState } from "@tanstack/react-router";
import { ChevronRight, Dumbbell, Loader2, X } from "lucide-react";
import { useState } from "react";

/**
 * A slim, sticky mobile-only banner below the header.
 * One-tap access to the user's active workout program.
 * Only visible on the homepage, dismissable per session.
 */
export function ActiveProgramBanner() {
  const { user } = useRouteContext({ from: "/_app" });
  const [dismissed, setDismissed] = useState(false);

  const { location, resolvedLocation, status } = useRouterState();

  // Show banner if the currently resolved (rendered) page is the homepage
  const isHomePage = resolvedLocation?.pathname === "/";

  const { data: activeProgramData } = useQuery({
    ...getActiveUserProgramOptions(),
    enabled: !!user && isHomePage && !dismissed,
  });

  const activeProgram = activeProgramData?.body;
  const hasActiveProgram =
    activeProgram &&
    !("message" in activeProgram) &&
    "programId" in activeProgram;

  // Detect if we are currently navigating to the program in this banner
  const isPending =
    status === "pending" &&
    isHomePage &&
    activeProgram &&
    !("message" in activeProgram) &&
    location.pathname.includes(activeProgram.programId);

  if (!user || !isHomePage || dismissed || !hasActiveProgram) return null;

  return (
    <div className="mt-20 flex flex-col md:hidden">
      <div className="w-full">
        <div className="flex items-center border-border/40 border-b bg-primary/5 backdrop-blur-sm">
          <Link
            to="/program/$programId"
            params={{ programId: activeProgram.programId }}
            className="flex min-w-0 flex-1 items-center gap-2.5 py-2 pl-4 transition-colors active:bg-primary/10"
          >
            {isPending ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
            ) : (
              <Dumbbell className="h-3.5 w-3.5 shrink-0 text-primary" />
            )}
            <span className="truncate font-medium text-foreground text-xs">
              {activeProgram.program?.name || "Aktif Program"}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              H{activeProgram.currentWeek}/G
              {(activeProgram.currentDayIndex ?? 0) + 1}
            </span>
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="flex h-full items-center px-4 py-2 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Kapat"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
